import { useEffect } from 'react';
import { useFetcher, useLocation } from 'react-router';
import {
    ENQUIRY_FORM_API_ROUTE,
    type EnquiryFormApiResponse,
} from '~/routes/remix-api.enquiry-form';
import { useFeature } from '~/modules/root/hook/use-feature';
import { EnquiryFormPageType, EnquiryFormTrigger } from 'generated/graphql';
import {
    PAGE_TYPE_BY_ITEM_TYPE,
    type EnquiryFormConfig,
    type PublicEnquiryForm,
} from '../types/public-enquiry-form';
import {
    usePageCtasContext,
    type PageCtasContextValue,
} from '../component/PageCtasProvider';
import type { UseEnquiryProps } from './use-enquiry';

export interface UseEnquiryFormForPageOptions {
    professionalId: number | null | undefined;
    /**
     * The Forms page type. Defaults to the page the visitor is on (from the
     * route), then to `itemType`.
     */
    pageType?: EnquiryFormPageType;
    /**
     * The enquiry's library item type. Only used as the page-type fallback
     * off a recognised public route, since e.g. a project page's "Message the
     * professional" CTA still belongs to the Project cell.
     */
    itemType?: UseEnquiryProps['itemType'];
    trigger?: EnquiryFormTrigger;
    /**
     * The product a product page shows. Its own choice for the button (a
     * chosen form, or hidden) wins over the Product page type's form; ignored
     * on other page types.
     */
    productId?: number | null;
    /** Skip the lookup entirely (e.g. the caller already has a form). */
    enabled?: boolean;
}

export interface EnquiryFormForPage {
    /** True until the lookup has answered. Always false when Forms is off. */
    isLoading: boolean;
    /** The professional switched this CTA off for this page type. */
    isOff: boolean;
    /** The configured form, or null under legacy / off. */
    form: PublicEnquiryForm | null;
    status: EnquiryFormApiResponse['status'];
}

const LEGACY: EnquiryFormForPage = {
    isLoading: false,
    isOff: false,
    form: null,
    status: 'legacy',
};

const PAGE_TYPE_BY_ROUTE_PREFIX: ReadonlyArray<
    readonly [prefix: string, pageType: EnquiryFormPageType]
> = [
    ['/project/', EnquiryFormPageType.Project],
    ['/product/', EnquiryFormPageType.Product],
    ['/professional/', EnquiryFormPageType.Professional],
    ['/article/', EnquiryFormPageType.Article],
];

/** The Forms page type of a public detail route, if it is one. */
export function pageTypeFromPath(
    pathname: string,
): EnquiryFormPageType | undefined {
    return PAGE_TYPE_BY_ROUTE_PREFIX.find(([prefix]) =>
        pathname.startsWith(prefix),
    )?.[1];
}

/**
 * The lookup URL for a page's CTA form. A product ID is only sent for
 * product pages, where the product's own choice for the button can win.
 */
export function enquiryFormForPageUrl({
    professionalId,
    pageType,
    trigger,
    productId,
}: {
    professionalId: number;
    pageType: EnquiryFormPageType | '';
    trigger: EnquiryFormTrigger;
    productId?: number | null;
}): string {
    const params = new URLSearchParams({
        professionalId: String(professionalId),
        pageType,
        trigger,
    });
    if (pageType === EnquiryFormPageType.Product && productId) {
        params.set('productId', String(productId));
    }
    return `${ENQUIRY_FORM_API_ROUTE}?${params.toString()}`;
}

interface FormCell {
    professionalId: number;
    pageType: EnquiryFormPageType;
    trigger: EnquiryFormTrigger;
    productId: number | null;
}

/**
 * The detail page's loader already resolved both of its buttons for its own
 * professional, page type and product. A lookup for that same cell takes the
 * loader's answer, so the buttons and every other surface on the page agree
 * and nothing is fetched again.
 */
export function formFromPageCtas(
    page: PageCtasContextValue | null,
    cell: FormCell,
): EnquiryFormConfig | undefined {
    if (!page?.ctas.fromServer || page.ctas.pageType !== cell.pageType) {
        return undefined;
    }
    if (Number(page.enquiry.professional.ID) !== cell.professionalId) {
        return undefined;
    }
    if (
        cell.pageType === EnquiryFormPageType.Product &&
        cell.productId !== page.enquiry.siteTreeID
    ) {
        return undefined;
    }
    const cta =
        cell.trigger === EnquiryFormTrigger.BookMeeting
            ? page.ctas.book
            : page.ctas.enquire;
    return cta.formConfig;
}

/**
 * Resolves the enquiry form a public page's CTA opens, once Forms is on.
 * Inside a detail page's `PageCtasProvider` the loader's answer serves the
 * page's own cell; anything else is fetched lazily. Callers keep their
 * flat-field behaviour until the answer lands and whenever the flag is off.
 */
export function useEnquiryFormForPage({
    professionalId,
    pageType,
    itemType,
    trigger = EnquiryFormTrigger.Enquire,
    productId,
    enabled = true,
}: UseEnquiryFormForPageOptions): EnquiryFormForPage {
    const isFormsEnabled = useFeature('bm_forms');
    const { pathname } = useLocation();
    const resolvedPageType =
        pageType ??
        pageTypeFromPath(pathname) ??
        (itemType ? PAGE_TYPE_BY_ITEM_TYPE[itemType] : undefined);
    const resolvedProductId =
        resolvedPageType === EnquiryFormPageType.Product && productId
            ? productId
            : null;
    const fetcher = useFetcher<EnquiryFormApiResponse>({
        key: `enquiry-form:${professionalId ?? 0}:${resolvedPageType ?? ''}:${trigger}:${resolvedProductId ?? 0}`,
    });
    const isWanted =
        enabled && isFormsEnabled && !!professionalId && !!resolvedPageType;
    const fromPage = useFormFromPage(
        isWanted && resolvedPageType
            ? {
                  professionalId: Number(professionalId),
                  pageType: resolvedPageType,
                  trigger,
                  productId: resolvedProductId,
              }
            : null,
    );
    const shouldFetch = isWanted && !fromPage;

    useEffect(() => {
        if (!shouldFetch || fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        fetcher.load(
            enquiryFormForPageUrl({
                professionalId: Number(professionalId),
                pageType: resolvedPageType ?? '',
                trigger,
                productId: resolvedProductId,
            }),
        );
        // The fetcher's state changes must not restart the load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        shouldFetch,
        professionalId,
        resolvedPageType,
        trigger,
        resolvedProductId,
    ]);

    if (fromPage) {
        return answered(fromPage);
    }
    if (!shouldFetch) {
        return LEGACY;
    }
    if (!fetcher.data) {
        return { ...LEGACY, isLoading: true };
    }

    return answered(fetcher.data);
}

function useFormFromPage(cell: FormCell | null): EnquiryFormConfig | undefined {
    const page = usePageCtasContext();
    return cell ? formFromPageCtas(page, cell) : undefined;
}

function answered(config: EnquiryFormConfig): EnquiryFormForPage {
    return {
        isLoading: false,
        isOff: config.status === 'off',
        form: config.form,
        status: config.status,
    };
}
